import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const OPEN_DELAY_MS = 100;
const CLOSE_DELAY_MS = 150;
const VIEWPORT_MARGIN = 8;
const GAP = 8;
const ARROW = 8;
const ARROW_INSET = 12;

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';
type ResolvedSide = TooltipPlacement;

interface Position {
  left: number;
  top: number;
  side: ResolvedSide;
  arrow: number;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));

function computePosition(
  trigger: DOMRect,
  tip: DOMRect,
  placement: TooltipPlacement,
): Position {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const horizontal = placement === 'left' || placement === 'right';

  if (horizontal) {
    const fitsRight = trigger.right + GAP + tip.width <= vw - VIEWPORT_MARGIN;
    const fitsLeft = trigger.left - GAP - tip.width >= VIEWPORT_MARGIN;
    const side: ResolvedSide =
      placement === 'right' ? (fitsRight || !fitsLeft ? 'right' : 'left') : fitsLeft || !fitsRight ? 'left' : 'right';
    const top = clamp(
      trigger.top + trigger.height / 2 - tip.height / 2,
      VIEWPORT_MARGIN,
      Math.max(VIEWPORT_MARGIN, vh - tip.height - VIEWPORT_MARGIN),
    );
    return {
      left: side === 'right' ? trigger.right + GAP : trigger.left - GAP - tip.width,
      top,
      side,
      arrow: clamp(
        trigger.top + trigger.height / 2 - top,
        ARROW_INSET,
        Math.max(ARROW_INSET, tip.height - ARROW_INSET),
      ),
    };
  }

  const fitsBelow = trigger.bottom + GAP + tip.height <= vh - VIEWPORT_MARGIN;
  const fitsAbove = trigger.top - GAP - tip.height >= VIEWPORT_MARGIN;
  const side: ResolvedSide =
    placement === 'bottom' ? (fitsBelow || !fitsAbove ? 'bottom' : 'top') : fitsAbove || !fitsBelow ? 'top' : 'bottom';
  const left = clamp(
    trigger.left + trigger.width / 2 - tip.width / 2,
    VIEWPORT_MARGIN,
    Math.max(VIEWPORT_MARGIN, vw - tip.width - VIEWPORT_MARGIN),
  );
  return {
    left,
    top: side === 'bottom' ? trigger.bottom + GAP : trigger.top - GAP - tip.height,
    side,
    arrow: clamp(
      trigger.left + trigger.width / 2 - left,
      ARROW_INSET,
      Math.max(ARROW_INSET, tip.width - ARROW_INSET),
    ),
  };
}

const ARROW_BORDERS: Record<ResolvedSide, string> = {
  bottom: 'border-l border-t',
  top: 'border-r border-b',
  right: 'border-b border-l',
  left: 'border-r border-t',
};

function arrowStyle(pos: Position): CSSProperties {
  const off = -ARROW / 2 - 1;
  switch (pos.side) {
    case 'bottom':
      return { top: off, left: pos.arrow - ARROW / 2 };
    case 'top':
      return { bottom: off, left: pos.arrow - ARROW / 2 };
    case 'right':
      return { left: off, top: pos.arrow - ARROW / 2 };
    case 'left':
      return { right: off, top: pos.arrow - ARROW / 2 };
  }
}

export function useTooltip(content: ReactNode, placement: TooltipPlacement, maxWidth: string) {
  const id = useId();
  const tipRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const openNow = useCallback(() => {
    clearTimer();
    setOpen(true);
  }, [clearTimer]);

  const openDelayed = useCallback(() => {
    clearTimer();
    timer.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }, [clearTimer]);

  const close = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  const closeDelayed = useCallback(() => {
    clearTimer();
    timer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [clearTimer]);

  const cancelClose = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    setPos(computePosition(anchor.getBoundingClientRect(), tip.getBoundingClientRect(), placement));
  }, [open, content, placement]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  const portal =
    open &&
    createPortal(
      <div
        ref={tipRef}
        id={id}
        role="tooltip"
        onMouseEnter={cancelClose}
        onMouseLeave={closeDelayed}
        style={{
          position: 'fixed',
          left: pos?.left ?? 0,
          top: pos?.top ?? 0,
          maxWidth,
          visibility: pos ? 'visible' : 'hidden',
          zIndex: 9999,
        }}
        className="pointer-events-auto rounded-xl border border-slate-700/80 bg-slate-900 px-3 py-2 text-[11px] leading-relaxed text-slate-100 shadow-xl backdrop-blur-md transition-opacity duration-150 animate-in fade-in"
      >
        {pos && (
          <span
            aria-hidden
            style={arrowStyle(pos)}
            className={cn(
              'absolute h-2 w-2 rotate-45 border-slate-700/80 bg-slate-900',
              ARROW_BORDERS[pos.side],
            )}
          />
        )}
        <span className="relative block">{content}</span>
      </div>,
      document.body,
    );

  return { id, open, portal, anchorRef, openNow, openDelayed, close, closeDelayed, cancelClose };
}

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  className?: string;
  placement?: TooltipPlacement;
  maxWidth?: string;
  triggerLabel?: string;
}

export function Tooltip({
  content,
  children,
  className,
  placement = 'top',
  maxWidth = '16rem',
  triggerLabel,
}: TooltipProps) {
  const t = useTooltip(content, placement, maxWidth);
  return (
    <>
      <span
        ref={t.anchorRef as React.Ref<HTMLSpanElement>}
        aria-describedby={t.open ? t.id : undefined}
        aria-label={triggerLabel}
        onMouseEnter={t.openDelayed}
        onMouseLeave={t.closeDelayed}
        onFocus={t.openNow}
        onBlur={t.close}
        onClick={(e) => {
          e.stopPropagation();
          t.open ? t.close() : t.openNow();
        }}
        className={cn(
          'inline-flex cursor-help items-center focus-visible:outline-none',
          className,
        )}
      >
        {children}
      </span>
      {t.portal}
    </>
  );
}
